import { useEffect, useRef, type CSSProperties } from 'react';
import { Button } from '../../components/Button';
import { useSound } from '../../hooks/useSound';
import type { BoardProps } from '../../engine/types';
import { foundCells, type BrambleMove, type BrambleState } from './logic';

export function BrambleBoard({ state, status, dispatch, feedback }: BoardProps<BrambleState, BrambleMove>) {
  const playing = status === 'playing';
  const sound = useSound();
  const found = foundCells(state);
  const prevFound = useRef(state.found.length);

  useEffect(() => {
    if (state.found.length > prevFound.current) sound.play('reveal');
    prevFound.current = state.found.length;
  }, [state.found.length, sound]);

  const onTap = (index: number) => {
    if (!playing) return;
    sound.play('select');
    dispatch({ type: 'tap', index });
  };

  return (
    <div className="cv-board">
      <div className="cv-bramble">
        <div
          className="cv-bramble__grid"
          style={{ '--cols': state.size } as CSSProperties}
          role="group"
          aria-label="Word search grid"
        >
          {state.grid.map((ch, i) => (
            <button
              key={i}
              type="button"
              className="cv-bramble__cell"
              data-sel={state.selStart === i ? 'true' : undefined}
              data-found={found.has(i) ? 'true' : undefined}
              data-hint={state.revealedCell === i ? 'true' : undefined}
              onClick={() => onTap(i)}
              disabled={!playing}
              aria-label={`Row ${Math.floor(i / state.size) + 1}, column ${(i % state.size) + 1}, letter ${ch}`}
            >
              {ch.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="cv-bramble__words" role="list" aria-label="Words to find">
          {state.words.map((w) => (
            <span
              key={w}
              role="listitem"
              className="cv-bramble__word"
              data-found={state.found.includes(w) ? 'true' : undefined}
            >
              {w.toUpperCase()}
            </span>
          ))}
        </div>

        {playing && state.found.length > 0 && state.found.length < state.words.length ? (
          <Button variant="ghost" onClick={() => dispatch({ type: 'finish' })}>
            Finish · {state.found.length}/{state.words.length} found
          </Button>
        ) : null}
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>
    </div>
  );
}
